import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useSlug } from "@/hooks/useSlug";
import { documentMutations } from "@/queries/documents/mutations";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { routes } from "@/routes";

import {
  type CreateOrEditFolderFormValues,
  DocumentFolderForm,
  type DocumentFolderFormProps,
} from "./DocumentFolderForm";
import { getDefaultFormValues } from "./Manager";

export function CreateDocumentFolderPage(): React.ReactNode {
  const slug = useSlug();
  const { folderId: parentFolderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();

  const createFolderMutation = documentMutations.useCreateFolder(parentFolderId!);

  const documentQueries = useDocumentQueries();
  const {
    data: parentFolder,
    isPending: parentFolderIsPending,
    error: parentFolderError,
  } = useQuery({
    ...documentQueries.listDocuments({
      folderId: parentFolderId!,
    }),
    enabled: !!parentFolderId,
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        id: data.id,
        name: data.name,
      };
    },
  });
  const defaultFormValues = getDefaultFormValues(parentFolderId!);
  function onSubmit(data: CreateOrEditFolderFormValues) {
    const request = {
      name: data.name,
      nameTranslations: [],
      parentFolderId: data.parentFolderId,
    };

    return createFolderMutation.mutate(request, {
      onSuccess: () => {
        void navigate(routes.documents.documentsList({ slug, folderId: parentFolderId! }));
      },
    });
  }

  if (parentFolderError) {
    return <ErrorPage error={parentFolderError} />;
  }

  if (parentFolderIsPending) {
    return <FullSizeLoader withPadding />;
  }

  const formProps: DocumentFolderFormProps = {
    folderId: parentFolder.id,
    folderName: parentFolder?.name,
    onSubmit,
    originalLanguageId: undefined,
    defaultFormValues,
    isEditMode: false,
    isSubmitting: createFolderMutation.isPending,
  };

  return <DocumentFolderForm {...formProps} />;
}
